import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getFirebaseInstances } from './config';
import { getCurrentUser } from './auth';
import type { Ranking } from '@/schemas';
import { logger } from '@/utils/logger';

const RANKINGS_COLLECTION = 'rankings';
const TOP_RANKINGS_LIMIT = 10;

/**
 * スコアをFirestoreに保存
 */
export async function saveScore(score: number, playerName: string): Promise<boolean> {
  const firebase = getFirebaseInstances();
  if (!firebase) {
    logger.warn('Firebase is not initialized');
    return false;
  }

  const user = getCurrentUser();
  if (!user) {
    logger.error('User not authenticated');
    return false;
  }

  try {
    const rankingData = {
      score,
      playerName,
      userId: user.uid,
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now(),
    };

    await addDoc(collection(firebase.db, RANKINGS_COLLECTION), rankingData);
    logger.log('Score saved successfully:', score);
    return true;
  } catch (error) {
    logger.error('Error saving score:', error);
    return false;
  }
}

/**
 * トップ10のランキングを取得
 */
export async function getTopRankings(): Promise<Ranking[]> {
  const firebase = getFirebaseInstances();
  if (!firebase) {
    logger.warn('Firebase is not initialized');
    return [];
  }

  try {
    const q = query(
      collection(firebase.db, RANKINGS_COLLECTION),
      orderBy('score', 'desc'),
      limit(TOP_RANKINGS_LIMIT)
    );

    const querySnapshot = await getDocs(q);
    const rankings: Ranking[] = [];

    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      const data = doc.data();
      rankings.push({
        score: data.score,
        playerName: data.playerName,
        userId: data.userId,
        timestamp: data.timestamp.toDate(),
        createdAt: data.createdAt.toDate(),
      });
    });

    logger.log(`Retrieved ${rankings.length} rankings`);
    return rankings;
  } catch (error) {
    logger.error('Error getting rankings:', error);
    return [];
  }
}

/**
 * スコアがトップ10に入るかチェック
 */
export async function isTopScore(score: number): Promise<boolean> {
  const rankings = await getTopRankings();

  // ランキングが10件未満の場合は自動的にトップ10入り
  if (rankings.length < TOP_RANKINGS_LIMIT) {
    return true;
  }

  // 10位のスコアより高いかチェック
  const lowestTopScore = rankings[rankings.length - 1].score;
  return score > lowestTopScore;
}
